/**
 * Timezone utility — display times in the user's configured timezone.
 *
 * All timestamps are stored as UTC in the database. Every date/time rendered
 * to the user is converted to their chosen IANA timezone (defaulting to
 * America/Los_Angeles for LA/USA) before display.
 *
 * The active timezone is held as a module-level default that `TimezoneSync`
 * (wired into the app shell via `useAuth`) keeps in step with the
 * `user.timezone` column. Every formatter also accepts an explicit `tz`
 * argument for isolated call sites (e.g. coach-side views rendering another
 * user's times) that don't want to rely on the module-level default.
 *
 * Usage:
 *   import { formatDate, formatTime, formatDateTime, formatRelative, setDisplayTimezone } from "@/lib/timezone";
 */

export const DEFAULT_TZ = "America/Los_Angeles";

let activeTz: string = DEFAULT_TZ;

/**
 * Update the module-level display timezone. Invoked by `TimezoneSync` (or
 * manually in tests) whenever the authenticated user's preference changes.
 * Falsy values reset back to the Pacific default rather than crashing
 * `Intl.DateTimeFormat`.
 */
export function setDisplayTimezone(tz: string | null | undefined): void {
  activeTz = tz && tz.length > 0 ? tz : DEFAULT_TZ;
}

/** Read the currently-active display timezone. */
export function getDisplayTimezone(): string {
  return activeTz;
}

/**
 * Format a timestamp as a date string in the active timezone.
 * e.g. "Mar 31, 2026"
 */
export function formatDate(
  date: Date | string | number | null | undefined,
  options: Intl.DateTimeFormatOptions & { tz?: string } = {}
): string {
  if (!date) return "";
  const { tz = activeTz, ...rest } = options;
  const d = toDate(date);
  return d.toLocaleDateString("en-US", {
    timeZone: tz,
    month: "short",
    day: "numeric",
    year: "numeric",
    ...rest,
  });
}

/**
 * Format a timestamp as a short date in the active timezone.
 * e.g. "3/31/26"
 */
export function formatShortDate(
  date: Date | string | number | null | undefined,
  opts: { tz?: string } = {}
): string {
  if (!date) return "";
  const d = toDate(date);
  return d.toLocaleDateString("en-US", {
    timeZone: opts.tz ?? activeTz,
    month: "numeric",
    day: "numeric",
    year: "2-digit",
  });
}

/**
 * Format a timestamp as a time string in the active timezone.
 * e.g. "9:30 AM"
 */
export function formatTime(
  date: Date | string | number | null | undefined,
  opts: { tz?: string } = {}
): string {
  if (!date) return "";
  const d = toDate(date);
  return d.toLocaleTimeString("en-US", {
    timeZone: opts.tz ?? activeTz,
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

/**
 * Format a timestamp as date + time in the active timezone.
 * e.g. "Mar 31, 2026 at 9:30 AM"
 */
export function formatDateTime(
  date: Date | string | number | null | undefined,
  opts: { tz?: string } = {}
): string {
  if (!date) return "";
  const tz = opts.tz ?? activeTz;
  const d = toDate(date);
  const datePart = d.toLocaleDateString("en-US", {
    timeZone: tz,
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const timePart = d.toLocaleTimeString("en-US", {
    timeZone: tz,
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
  return `${datePart} at ${timePart}`;
}

/**
 * Format a timestamp as a day label in the active timezone.
 * e.g. "Monday", "Tuesday"
 */
export function formatDayName(
  date: Date | string | number | null | undefined,
  opts: { tz?: string } = {}
): string {
  if (!date) return "";
  const d = toDate(date);
  return d.toLocaleDateString("en-US", {
    timeZone: opts.tz ?? activeTz,
    weekday: "long",
  });
}

/**
 * Format a timestamp as a short day label in the active timezone.
 * e.g. "Mon", "Tue"
 */
export function formatShortDayName(
  date: Date | string | number | null | undefined,
  opts: { tz?: string } = {}
): string {
  if (!date) return "";
  const d = toDate(date);
  return d.toLocaleDateString("en-US", {
    timeZone: opts.tz ?? activeTz,
    weekday: "short",
  });
}

/**
 * Format a timestamp as a month + year label in the active timezone.
 * e.g. "March 2026"
 */
export function formatMonthYear(
  date: Date | string | number | null | undefined,
  opts: { tz?: string } = {}
): string {
  if (!date) return "";
  const d = toDate(date);
  return d.toLocaleDateString("en-US", {
    timeZone: opts.tz ?? activeTz,
    month: "long",
    year: "numeric",
  });
}

/**
 * Format a timestamp as a relative time string.
 * e.g. "just now", "5 min ago", "2 hours ago", "yesterday", "Mar 31"
 */
export function formatRelative(date: Date | string | number | null | undefined): string {
  if (!date) return "";
  const d = toDate(date);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffHr / 24);

  if (diffSec < 60) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDays === 1) return "yesterday";
  if (diffDays < 7) return `${diffDays}d ago`;

  return formatDate(d, { month: "short", day: "numeric", year: undefined });
}

/**
 * Get today's date string in the active timezone as YYYY-MM-DD.
 * Useful for comparing dates without time. Aliased as `todayInPST` for
 * backwards compatibility with older call sites.
 */
export function todayInUserTimezone(tz: string = activeTz): string {
  const now = new Date();
  return now.toLocaleDateString("en-CA", { timeZone: tz }); // en-CA gives YYYY-MM-DD
}

/** @deprecated Use todayInUserTimezone(). Kept so existing imports keep working. */
export const todayInPST = todayInUserTimezone;


export function addDaysToDateString(dateStr: string, days: number): string {
  const [year, month, day] = dateStr.split("-").map(Number);
  const d = new Date(Date.UTC(year, month - 1, day + days, 12, 0, 0));
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

/** YYYY-MM-DD in the active timezone for any timestamp. */
export function toDateStringInUserTimezone(
  date: Date | string | number | null | undefined,
  tz: string = activeTz
): string {
  if (!date) return "";
  const d = toDate(date);
  return d.toLocaleDateString("en-CA", { timeZone: tz });
}

/** @deprecated Use toDateStringInUserTimezone(). Kept for back-compat. */
export const toDateStringPST = toDateStringInUserTimezone;

function partsInTimezone(date: Date, tz: string): Record<string, number> {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(date);

  return Object.fromEntries(
    parts
      .filter((part) => part.type !== "literal")
      .map((part) => [part.type, Number(part.value)])
  );
}

/**
 * Convert a wall-clock date/time in a specific timezone into the UTC Date that
 * represents that moment. This keeps date pickers from drifting when the user
 * enters a Pacific schedule time like 5:00 PM.
 */
export function zonedDateTimeToUtc(
  dateStr: string,
  timeStr: string = "00:00",
  tz: string = activeTz
): Date {
  const [year, month, day] = dateStr.split("-").map(Number);
  const [hour = 0, minute = 0] = timeStr.split(":").map(Number);
  const utcGuess = new Date(Date.UTC(year, month - 1, day, hour, minute, 0, 0));
  const parts = partsInTimezone(utcGuess, tz);
  const wallAsUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second,
    0
  );
  const offsetMs = wallAsUtc - utcGuess.getTime();
  return new Date(utcGuess.getTime() - offsetMs);
}

export function startOfDayInUserTimezone(dateStr: string, tz: string = activeTz): Date {
  return zonedDateTimeToUtc(dateStr, "00:00", tz);
}

export function endOfDayInUserTimezone(dateStr: string, tz: string = activeTz): Date {
  const start = startOfDayInUserTimezone(dateStr, tz);
  return new Date(start.getTime() + 24 * 60 * 60 * 1000 - 1);
}

/** Check if a timestamp is today in the active timezone. */
export function isToday(date: Date | string | number | null | undefined, tz: string = activeTz): boolean {
  if (!date) return false;
  return toDateStringInUserTimezone(date, tz) === todayInUserTimezone(tz);
}

/** @deprecated Use isToday(). Kept for back-compat. */
export const isTodayPST = isToday;

/** Check if a timestamp is yesterday in the active timezone. */
export function isYesterday(date: Date | string | number | null | undefined, tz: string = activeTz): boolean {
  if (!date) return false;
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return toDateStringInUserTimezone(date, tz) === yesterday.toLocaleDateString("en-CA", { timeZone: tz });
}

/** @deprecated Use isYesterday(). Kept for back-compat. */
export const isYesterdayPST = isYesterday;

/**
 * Format a timestamp with smart labels: "Today", "Yesterday", or the date.
 * e.g. "Today", "Yesterday", "Mar 29"
 */
export function formatSmartDate(date: Date | string | number | null | undefined): string {
  if (!date) return "";
  if (isToday(date)) return "Today";
  if (isYesterday(date)) return "Yesterday";
  return formatDate(date, { month: "short", day: "numeric", year: undefined });
}

/**
 * Format a timestamp for use in a message thread.
 * e.g. "Today at 9:30 AM", "Yesterday at 2:15 PM", "Mar 29 at 11:00 AM"
 */
export function formatMessageTime(date: Date | string | number | null | undefined): string {
  if (!date) return "";
  const smartDate = formatSmartDate(date);
  const time = formatTime(date);
  return `${smartDate} at ${time}`;
}

// ── Internal helpers ──────────────────────────────────────────────────────────

function toDate(value: Date | string | number): Date {
  if (value instanceof Date) {
    // MySQL DATE columns come through superjson as a real JS Date pinned to
    // UTC midnight (e.g. `2026-06-02T00:00:00.000Z`). Treat those as calendar
    // dates — anchor to noon UTC so they don't drift backwards a day when
    // rendered in a negative-offset zone like PST/PDT. We only do this when
    // every UTC time component is zero, so genuine "midnight UTC" instants
    // remain rare false-positives.
    if (
      value.getUTCHours() === 0 &&
      value.getUTCMinutes() === 0 &&
      value.getUTCSeconds() === 0 &&
      value.getUTCMilliseconds() === 0
    ) {
      return new Date(Date.UTC(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate(), 12));
    }
    return value;
  }
  if (typeof value === "number") return new Date(value);
  if (typeof value === "string") {
    // Date-only strings like "2026-03-31" — anchored to noon UTC so they
    // land on the intended calendar day in every US timezone without
    // drifting backwards when converted to local time.
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return new Date(`${value}T12:00:00Z`);
    }
    // Same shape, but already padded to a full ISO string by JSON.stringify
    // when superjson is *not* in play.
    const dateOnly = value.match(/^(\d{4}-\d{2}-\d{2})T00:00:00(\.000)?Z$/);
    if (dateOnly) return new Date(`${dateOnly[1]}T12:00:00Z`);
  }
  return new Date(value);
}
